import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../core/contexts/AuthContext';
import { Header } from '../../shared/layout/Header';
import { uploadLocationImage, deleteLocationImage } from './services/storageService';
import {
  createLocation,
  getAllLocations,
  updateLocation,
  deleteLocation,
  permanentlyDeleteLocation
} from './services/locationService';
import './AdminPanel.css';

export default function AdminPanel() {
  const { currentUser, userProfile } = useAuth();
  const navigate = useNavigate();

  // Form state
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [locationName, setLocationName] = useState('');
  const [latitude, setLatitude] = useState('');
  const [longitude, setLongitude] = useState('');
  const [difficulty, setDifficulty] = useState('medium');
  const [tags, setTags] = useState('');

  // UI state
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Locations list state
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [showActiveOnly, setShowActiveOnly] = useState(true);

  // Auth loading state
  const [authChecking, setAuthChecking] = useState(true);

  // Check if user is admin (you can implement your own admin check logic)
  useEffect(() => {
    // Give auth context time to load
    const timer = setTimeout(() => {
      setAuthChecking(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Redirect if not authenticated after auth check completes
  useEffect(() => {
    if (!authChecking && !currentUser) {
      // User is definitely not logged in, redirect after a moment
      const timer = setTimeout(() => {
        navigate('/');
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [authChecking, currentUser, navigate]);

  // Load all locations
  useEffect(() => {
    loadLocations();
  }, [showActiveOnly]);

  const loadLocations = async () => {
    try {
      setLoadingLocations(true);
      const allLocations = await getAllLocations({ activeOnly: showActiveOnly });
      setLocations(allLocations);
    } catch (error) {
      console.error('Error loading locations:', error);
    } finally {
      setLoadingLocations(false);
    }
  };

  // Handle image file selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      setUploadError('Please select a JPEG, PNG, or WebP image.');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('Image must be smaller than 5MB.');
      return;
    }

    setImageFile(file);
    setUploadError(null);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setUploadError(null);
    setUploadSuccess(false);

    // Validate form
    if (!imageFile) {
      setUploadError('Please select an image.');
      return;
    }

    if (!locationName.trim()) {
      setUploadError('Please enter a location name.');
      return;
    }

    // Validate coordinates
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (!latitude || isNaN(lat) || lat < -90 || lat > 90) {
      setUploadError('Please enter a valid latitude (between -90 and 90).');
      return;
    }

    if (!longitude || isNaN(lng) || lng < -180 || lng > 180) {
      setUploadError('Please enter a valid longitude (between -180 and 180).');
      return;
    }

    try {
      setUploading(true);

      // Generate a temporary location ID (timestamp-based)
      const locationId = `loc_${Date.now()}`;

      // Upload image to Firebase Storage
      const { storagePath, downloadUrl } = await uploadLocationImage(imageFile, locationId);

      // Parse tags
      const tagArray = tags
        .split(',')
        .map(tag => tag.trim())
        .filter(tag => tag.length > 0);

      // Create location in Firestore
      const docId = await createLocation({
        imageStoragePath: storagePath,
        imageUrl: downloadUrl,
        actualLocation: {
          lat: lat,
          lng: lng,
          name: locationName.trim()
        },
        difficulty: difficulty,
        tags: tagArray
      });

      console.log('✅ Location created successfully:', docId);

      // Reset form
      setImageFile(null);
      setImagePreview(null);
      setLocationName('');
      setLatitude('');
      setLongitude('');
      setDifficulty('medium');
      setTags('');

      setUploadSuccess(true);

      // Reload locations list
      await loadLocations();

      // Clear success message after 3 seconds
      setTimeout(() => setUploadSuccess(false), 3000);
    } catch (error) {
      console.error('Error creating location:', error);
      setUploadError(error.message || 'Failed to create location. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  // Handle location deletion
  const handleDeleteLocation = async (locationId, storagePath) => {
    if (!window.confirm('Are you sure you want to delete this location? This will soft-delete it (set isActive to false).')) {
      return;
    }

    try {
      await deleteLocation(locationId);
      await loadLocations();
    } catch (error) {
      console.error('Error deleting location:', error);
      alert('Failed to delete location: ' + error.message);
    }
  };

  // Handle permanent deletion
  const handlePermanentDelete = async (locationId, storagePath) => {
    if (!window.confirm('PERMANENTLY DELETE this location and its image? This cannot be undone!')) {
      return;
    }

    try {
      // Delete from storage
      if (storagePath) {
        await deleteLocationImage(storagePath);
      }

      // Delete from database
      await permanentlyDeleteLocation(locationId);

      await loadLocations();
    } catch (error) {
      console.error('Error permanently deleting location:', error);
      alert('Failed to permanently delete location: ' + error.message);
    }
  };

  // Handle reactivate location
  const handleReactivateLocation = async (locationId) => {
    try {
      await updateLocation(locationId, { isActive: true });
      await loadLocations();
    } catch (error) {
      console.error('Error reactivating location:', error);
      alert('Failed to reactivate location: ' + error.message);
    }
  };

  // Show loading while checking auth
  if (authChecking) {
    return (
      <div className="admin-panel-page">
        <Header />
        <div className="admin-panel-container">
          <div className="loading">Checking authentication...</div>
        </div>
      </div>
    );
  }

  // Show login required message
  if (!currentUser) {
    return (
      <div className="admin-panel-page">
        <Header />
        <div className="admin-panel-container">
          <div className="admin-header">
            <h1>Campus Guesser Admin Panel</h1>
            <p className="admin-subtitle">Upload and manage campus location photos</p>
          </div>
          <div className="alert alert-error" style={{ maxWidth: '600px', margin: '2rem auto', textAlign: 'center' }}>
            <h3>Authentication Required</h3>
            <p>You must be logged in to access the admin panel.</p>
            <p>Redirecting to home page in 2 seconds...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-panel-page">
      <Header />
      <div className="admin-panel-container">
        <div className="admin-header">
          <h1>Campus Guesser Admin Panel</h1>
          <p className="admin-subtitle">Upload and manage campus location photos</p>
        </div>

        <div className="admin-content">
          {/* Upload Form Section */}
          <section className="upload-section">
            <h2>Upload New Location</h2>

            <form onSubmit={handleSubmit} className="upload-form">
              {/* Image Upload */}
              <div className="form-group">
                <label htmlFor="image-upload">Location Photo *</label>
                <input
                  id="image-upload"
                  type="file"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  onChange={handleImageSelect}
                  disabled={uploading}
                />
                {imagePreview && (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                  </div>
                )}
              </div>

              {/* Location Name */}
              <div className="form-group">
                <label htmlFor="location-name">Location Name *</label>
                <input
                  id="location-name"
                  type="text"
                  value={locationName}
                  onChange={(e) => setLocationName(e.target.value)}
                  placeholder="e.g., Founders Library"
                  disabled={uploading}
                  required
                />
              </div>

              {/* Coordinate Inputs */}
              <div className="form-group">
                <label htmlFor="latitude">Latitude *</label>
                <input
                  id="latitude"
                  type="number"
                  step="0.000001"
                  value={latitude}
                  onChange={(e) => setLatitude(e.target.value)}
                  placeholder="e.g., 38.92232 (Howard University: ~38.92)"
                  disabled={uploading}
                  required
                />
                <p className="help-text">Enter latitude between -90 and 90. Use Google Maps to find exact coordinates.</p>
              </div>

              <div className="form-group">
                <label htmlFor="longitude">Longitude *</label>
                <input
                  id="longitude"
                  type="number"
                  step="0.000001"
                  value={longitude}
                  onChange={(e) => setLongitude(e.target.value)}
                  placeholder="e.g., -77.01968 (Howard University: ~-77.02)"
                  disabled={uploading}
                  required
                />
                <p className="help-text">Enter longitude between -180 and 180. Tip: Right-click a location in Google Maps → Click coordinates to copy.</p>
              </div>

              {/* Difficulty */}
              <div className="form-group">
                <label htmlFor="difficulty">Difficulty</label>
                <select
                  id="difficulty"
                  value={difficulty}
                  onChange={(e) => setDifficulty(e.target.value)}
                  disabled={uploading}
                >
                  <option value="easy">Easy</option>
                  <option value="medium">Medium</option>
                  <option value="hard">Hard</option>
                </select>
              </div>

              {/* Tags */}
              <div className="form-group">
                <label htmlFor="tags">Tags (comma-separated)</label>
                <input
                  id="tags"
                  type="text"
                  value={tags}
                  onChange={(e) => setTags(e.target.value)}
                  placeholder="e.g., building, outdoor, landmark"
                  disabled={uploading}
                />
              </div>

              {/* Error/Success Messages */}
              {uploadError && <div className="alert alert-error">{uploadError}</div>}
              {uploadSuccess && <div className="alert alert-success">Location created successfully!</div>}

              {/* Submit Button */}
              <button type="submit" className="btn-primary" disabled={uploading}>
                {uploading ? 'Uploading...' : 'Create Location'}
              </button>
            </form>
          </section>

          {/* Locations List Section */}
          <section className="locations-section">
            <div className="section-header">
              <h2>Existing Locations</h2>
              <div className="filter-controls">
                <label>
                  <input
                    type="checkbox"
                    checked={showActiveOnly}
                    onChange={(e) => setShowActiveOnly(e.target.checked)}
                  />
                  Show active only
                </label>
              </div>
            </div>

            {loadingLocations ? (
              <div className="loading">Loading locations...</div>
            ) : locations.length === 0 ? (
              <div className="empty-state">No locations found. Upload your first location above!</div>
            ) : (
              <div className="locations-grid">
                {locations.map((location) => (
                  <div key={location.id} className={`location-card ${!location.isActive ? 'inactive' : ''}`}>
                    <div className="location-image">
                      <img src={location.imageUrl} alt={location.actualLocation.name} />
                      {!location.isActive && <div className="inactive-badge">Inactive</div>}
                    </div>
                    <div className="location-info">
                      <h3>{location.actualLocation.name}</h3>
                      <p className="location-coords">
                        Lat: {location.actualLocation.lat.toFixed(6)}, Lng: {location.actualLocation.lng.toFixed(6)}
                      </p>
                      <div className="location-meta">
                        <span className={`difficulty-badge ${location.difficulty}`}>{location.difficulty}</span>
                        {location.tags && location.tags.length > 0 && (
                          <div className="tags">
                            {location.tags.map((tag, idx) => (
                              <span key={idx} className="tag">{tag}</span>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="location-actions">
                        {location.isActive ? (
                          <button
                            className="btn-delete"
                            onClick={() => handleDeleteLocation(location.id, location.imageStoragePath)}
                          >
                            Deactivate
                          </button>
                        ) : (
                          <button
                            className="btn-success"
                            onClick={() => handleReactivateLocation(location.id)}
                          >
                            Reactivate
                          </button>
                        )}
                        <button
                          className="btn-danger"
                          onClick={() => handlePermanentDelete(location.id, location.imageStoragePath)}
                        >
                          Delete Forever
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
